import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { TermsOfService, UserTosAcceptance, UserTosStatus } from '../types/database';

interface UseTosReturn {
  activeTos: TermsOfService | null;
  allTosVersions: TermsOfService[];
  userTosStatus: UserTosStatus | null;
  loading: boolean;
  error: string | null;
  acceptTos: (tosId: string, ipAddress?: string, userAgent?: string) => Promise<{ success: boolean; error: string | null }>;
  createTosVersion: (version: string, title: string, content: string, isActive: boolean) => Promise<{ success: boolean; error: string | null }>;
  updateTosVersion: (tosId: string, updates: Partial<TermsOfService>) => Promise<{ success: boolean; error: string | null }>;
  activateTos: (tosId: string) => Promise<{ success: boolean; error: string | null }>;
  refreshActiveTos: () => Promise<void>;
  refreshUserStatus: () => Promise<void>;
}

export const useTermsOfService = (userId?: string): UseTosReturn => {
  const [activeTos, setActiveTos] = useState<TermsOfService | null>(null);
  const [allTosVersions, setAllTosVersions] = useState<TermsOfService[]>([]);
  const [userTosStatus, setUserTosStatus] = useState<UserTosStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch active TOS
  const fetchActiveTos = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('terms_of_service')
        .select('*')
        .eq('is_active', true)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 is "no rows returned", which is ok
        throw fetchError;
      }

      setActiveTos(data || null);
      return data;
    } catch (err: any) {
      console.error('Error fetching active TOS:', err);
      setError(err.message);
      return null;
    }
  };

  // Fetch all TOS versions (staff only)
  const fetchAllTosVersions = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('terms_of_service')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setAllTosVersions(data || []);
    } catch (err: any) {
      console.error('Error fetching all TOS versions:', err);
      // Don't set error here as this might be a permission issue
    }
  };

  // Fetch user TOS status
  const fetchUserTosStatus = async (uid: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('vw_user_tos_status')
        .select('*')
        .eq('user_id', uid)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      setUserTosStatus(data || null);
    } catch (err: any) {
      console.error('Error fetching user TOS status:', err);
      setError(err.message);
    }
  };

  // Initial load
  useEffect(() => {
    // Reset user status immediately when userId changes to prevent stale data
    // from causing the TOS modal to flash before the new status loads
    setUserTosStatus(null);

    const loadData = async () => {
      setLoading(true);
      setError(null);

      await fetchActiveTos();
      await fetchAllTosVersions();

      if (userId) {
        await fetchUserTosStatus(userId);
      }

      setLoading(false);
    };

    loadData();
  }, [userId]);

  // Accept TOS
  const acceptTos = async (tosId: string, ipAddress?: string, userAgent?: string) => {
    try {
      const { error: insertError } = await supabase
        .from('user_tos_acceptance')
        .insert({
          tos_id: tosId,
          user_id: userId,
          ip_address: ipAddress || null,
          user_agent: userAgent || null,
        });

      if (insertError) throw insertError;

      // Refresh user status
      if (userId) {
        await fetchUserTosStatus(userId);
      }

      return { success: true, error: null };
    } catch (err: any) {
      console.error('Error accepting TOS:', err);
      return { success: false, error: err.message };
    }
  };

  // Create new TOS version (staff only)
  const createTosVersion = async (
    version: string,
    title: string,
    content: string,
    isActive: boolean
  ) => {
    try {
      const { error: insertError } = await supabase
        .from('terms_of_service')
        .insert({
          version,
          title,
          content,
          is_active: isActive,
        });

      if (insertError) throw insertError;

      // Refresh data
      await fetchActiveTos();
      await fetchAllTosVersions();

      return { success: true, error: null };
    } catch (err: any) {
      console.error('Error creating TOS version:', err);
      return { success: false, error: err.message };
    }
  };

  // Update TOS version (staff only)
  const updateTosVersion = async (tosId: string, updates: Partial<TermsOfService>) => {
    try {
      const { error: updateError } = await supabase
        .from('terms_of_service')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('tos_id', tosId);

      if (updateError) throw updateError;

      // Refresh data
      await fetchActiveTos();
      await fetchAllTosVersions();

      return { success: true, error: null };
    } catch (err: any) {
      console.error('Error updating TOS version:', err);
      return { success: false, error: err.message };
    }
  };

  // Set active TOS (staff only)
  const activateTos = async (tosId: string) => {
    try {
      // First, deactivate all TOS versions
      const { error: deactivateError } = await supabase
        .from('terms_of_service')
        .update({ is_active: false })
        .neq('tos_id', '00000000-0000-0000-0000-000000000000'); // Update all

      if (deactivateError) throw deactivateError;

      // Then activate the selected one
      const { error: activateError } = await supabase
        .from('terms_of_service')
        .update({ is_active: true })
        .eq('tos_id', tosId);

      if (activateError) throw activateError;

      // Refresh data
      await fetchActiveTos();
      await fetchAllTosVersions();

      return { success: true, error: null };
    } catch (err: any) {
      console.error('Error setting active TOS:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    activeTos,
    allTosVersions,
    userTosStatus,
    loading,
    error,
    acceptTos,
    createTosVersion,
    updateTosVersion,
    activateTos,
    refreshActiveTos: fetchActiveTos,
    refreshUserStatus: () => userId ? fetchUserTosStatus(userId) : Promise.resolve(),
  };
};
